import { useEffect, useReducer } from "react";
import { toast } from "react-toastify";
import MessageBar from "../components/ui/MessageBar";

/**
 * Show the message bar: "Book removed" with an optional Undo.
 *
 * Prefer this over a confirmation pop-up for anything that can be undone.
 * The caller does the real work in `onClose` unless Undo was pressed, so the
 * change only lands once the bar is gone:
 *
 *   showMessageBar({
 *     text: "Book removed from your order",
 *     onUndo: () => putItBack(),
 *     onClose: ({ undone }) => { if (!undone) reallyRemove(); },
 *   });
 *
 * Only one bar shows at a time. A new bar replaces the one on screen, and the
 * replaced bar closes as if its timer ran out (its change lands). Pass a
 * `mergeKey` to fold repeats into the bar on screen instead: the same kind of
 * change made again while the bar is up joins it, `mergedText(count)` becomes
 * the text, the timer starts over, and Undo undoes every change in the bar,
 * newest first. Each change's `onClose` still runs once when the bar closes.
 *
 * Returns the toast id (pass to `dismissMessageBar` to close it early).
 */
let current = null;

/** Start closing a bar: from here on nothing merges into it. */
function closeBar(bar) {
  bar.closing = true;
  clearTimeout(bar.timer);
  toast.dismiss(bar.id);
}

function startTimer(bar) {
  clearTimeout(bar.timer);
  if (!bar.closing) bar.timer = setTimeout(() => closeBar(bar), bar.duration);
}

/** The bar's content; a merge changes `bar.text` and redraws it through `bar.redraw`. */
function LiveBar({ bar }) {
  const [, redraw] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    bar.redraw = redraw;
    return () => {
      if (bar.redraw === redraw) bar.redraw = null;
    };
  }, [bar]);
  const canUndo = bar.items.every((item) => item.onUndo);
  return (
    <MessageBar
      text={bar.text}
      undoLabel={bar.undoLabel}
      onUndo={
        canUndo
          ? () => {
              if (bar.closing) return;
              bar.undone = true;
              [...bar.items].reverse().forEach((item) => item.onUndo());
              closeBar(bar);
            }
          : null
      }
      onPause={() => clearTimeout(bar.timer)}
      onResume={() => startTimer(bar)}
    />
  );
}

export function showMessageBar({ text, undoLabel = "Undo", onUndo = null, onClose = null, duration = 5000, mergeKey = null, mergedText = null }) {
  if (current && mergeKey && current.mergeKey === mergeKey && !current.closing) {
    const bar = current;
    bar.items.push({ onUndo, onClose });
    bar.text = mergedText ? mergedText(bar.items.length) : text;
    bar.redraw?.();
    startTimer(bar);
    return bar.id;
  }

  if (current) closeBar(current);

  const bar = { text, undoLabel, mergeKey, duration, items: [{ onUndo, onClose }], undone: false, closing: false, timer: null, redraw: null, id: null };
  bar.id = toast(() => <LiveBar bar={bar} />, {
    position: "bottom-center",
    // The bar runs its own timer so a merge can start it over.
    autoClose: false,
    hideProgressBar: true,
    closeOnClick: false,
    closeButton: false,
    draggable: false,
    icon: false,
    theme: "dark",
    className: "!min-h-0 !rounded-[14px] !bg-md-dark !p-0 !pl-[18px] !pr-1.5 !shadow-[0_10px_30px_rgba(27,13,18,0.3)] !w-[calc(100%-32px)] md:!w-auto md:!min-w-[360px] mx-auto",
    bodyClassName: "!p-0 !m-0",
    onClose: () => {
      bar.closing = true;
      clearTimeout(bar.timer);
      if (current === bar) current = null;
      bar.items.forEach((item) => item.onClose?.({ undone: bar.undone }));
    },
  });
  current = bar;
  startTimer(bar);
  return bar.id;
}

/** Close a bar early (its change lands); with no id, the bar on screen. */
export function dismissMessageBar(id) {
  if (current && (!id || id === current.id)) closeBar(current);
  else if (id) toast.dismiss(id);
}
